import { mapCompanyType, useFindCompanySuspense } from '@api';
import { TouringCard, TouringCardHeader, TouringCardTitle } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumberField } from '@hypeit-gmbh/hypeit-ui';
import { Box, Button, Group, Stack, Switch } from '@mantine/core';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/companies/$id.finance';
import {
  TaxAndDutiesFormValuesType,
  useTaxAndDutiesSchema,
  useUpdateTaxAndDuties,
} from './hooks';

const TRANS_PATH = 'TAX_AND_DUTIES';

export const CompanyTaxAndDuties: React.FC = () => {
  const { t } = useTranslation('companyDetailsFinance');
  const { id } = Route.useParams();
  const { data: company } = useFindCompanySuspense(id, {
    query: { select: (response) => mapCompanyType(response.data) },
  });

  const { schema, defaultValues } = useTaxAndDutiesSchema(company);
  const { updateTaxAndDuties } = useUpdateTaxAndDuties(company.id);

  const methods = useForm<TaxAndDutiesFormValuesType>({
    resolver: zodResolver(schema),
    defaultValues,
    values: defaultValues,
  });

  const handleSubmit = async (data: TaxAndDutiesFormValuesType) => {
    await updateTaxAndDuties(data);
    methods.reset(data);
  };

  const isSaveButtonDisabled =
    !methods.formState.isDirty || methods.formState.isSubmitting;

  return (
    <TouringCard data-testid="company-tax-and-duties-card">
      <TouringCardHeader>
        <TouringCardTitle>{t(`${TRANS_PATH}.TITLE`)}</TouringCardTitle>
      </TouringCardHeader>
      <Box pt="md">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)}>
            <Stack>
              <NumberField<TaxAndDutiesFormValuesType>
                fieldName="vatRate"
                label={t(`${TRANS_PATH}.FIELDS.VAT_RATE.LABEL`)}
                placeholder={t(`${TRANS_PATH}.FIELDS.VAT_RATE.PLACEHOLDER`)}
                min={0}
                max={100}
                step={1}
                allowDecimal={false}
                allowNegative={false}
                clampBehavior="strict"
                suffix="%"
              />
              <Controller
                control={methods.control}
                name="isKskLiable"
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <Switch
                    ref={ref}
                    checked={value}
                    onChange={(event) => onChange(event.currentTarget.checked)}
                    onBlur={onBlur}
                    label={t(`${TRANS_PATH}.FIELDS.IS_KSK_LIABLE.LABEL`)}
                  />
                )}
              />
              <Group justify="flex-end" py="xs">
                <Button
                  type="submit"
                  disabled={isSaveButtonDisabled}
                  loading={methods.formState.isSubmitting}
                >
                  {t(`${TRANS_PATH}.SAVE_BUTTON`)}
                </Button>
              </Group>
            </Stack>
          </form>
        </FormProvider>
      </Box>
    </TouringCard>
  );
};
